import Bugsnag from "@bugsnag/js";
import { QueryCache, QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import "react-bootstrap-typeahead/css/Typeahead.bs5.css";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AddressAPI } from "./api/address";
import { XSRFHeader } from "./api/const";
import QueryKeys from "./api/queryKeys";
import { startupApi } from "./api/startup";
import { UserAPI } from "./api/user";
import { AuthRouter, RouterErrorBoundary, SMPWRoutes } from "./components/authRouter";
import { InfoBoardLazy, MeetingAttendanceLazy } from "./components/congregation/async";
import { E2EMasterPassword } from "./components/e2e";
import ForgotPWCode from "./components/forgotpwCode";
import { InviteAccept } from "./components/inviteAccept";
import HourglassLogin from "./components/login";
import { HourglassUserNav } from "./components/nav/nav";
import { ManageUserAuthLazy } from "./components/publishers/async";
import { CongRegistration } from "./components/register/registrationStep";
import { SuspenseFallback } from "./components/utils";
import "./css/App.css";
import LoadFixtures from "./fixtures/loader";
import { Month } from "./helpers/dateHelpers";
import { getStatusCode } from "./helpers/errors";
import HourglassGlobals, { GlobalContext, HGContext, HGGlobals, emptyGlobals } from "./helpers/globals";
import { storeLangGroupId } from "./helpers/langGroups";
import { localeForUser, stringToNameFormat } from "./helpers/locale";
import { setTheme } from "./helpers/setTheme";
import { nameOfUser } from "./helpers/user";
import { clearSignOutFlag, hasSignOutFlag, haveCookie, supportsCookies } from "./helpers/util";
import { reportsQueryKey } from "./query/report";
import { rqShouldRetry } from "./query/retry";
import "./scss/App.scss";
import Address from "./types/address";
import { FSGroup } from "./types/fsgroup";
import Report from "./types/report";
import User, { UserStatus } from "./types/user";
import { hasPermission } from "./helpers/hasPermission";
import { Permission } from "./types/permission";
import { CircuitLazy, SMPWRegistrationLazy, SMPWUserRegistrationLazy } from "./components/scheduling/async";
import { CoRegistration } from "./components/register/coRegistration";
import RegularPublisherHome from "./components/pubView/publisherHome";
import PublisherTerritory from "./components/pubView/territory";
import SharedTerritory from "./components/shareView/territory";
import { SMPWPreRegistration } from "./components/register/smpwRegistration";

type InitialLoadProps = {
  haveToken: boolean;
  onTokenChange: (have: boolean) => void;
};

const noBackend = import.meta.env.VITE_NO_BACKEND
  ? import.meta.env.VITE_NO_BACKEND.toLowerCase().trim().startsWith("t")
  : false;
const queryClient = getQueryClient();

function getQueryClient(): QueryClient {
  //if we're running in front-end dev mode, with no backend, staleTime is Infinity so nothing ever gets fetched
  //we just use the data loaded from LoadFixtures in App below
  const staleTime = noBackend ? Infinity : 15 * 1000;
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: staleTime,
        refetchOnWindowFocus: false,
        retry: rqShouldRetry,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        const errorCode = getStatusCode(error);
        if (errorCode === 404 && query.meta?.clearStoredLangGroupOn404) {
          storeLangGroupId(-1);
        }
      },
    }),
  });
}

function App() {
  const [haveToken, setHaveToken] = useState<boolean>(initialSetup);

  if (noBackend) {
    LoadFixtures(queryClient);
  }

  useEffect(() => {
    if (import.meta.env.DEV) {
      document.body.classList.add("dev");
    }
    if (import.meta.env.VITE_HG_ENV === "staging") {
      document.body.classList.add("staging");
    }
  }, []);

  const handleTokenChange = (have: boolean) => {
    setHaveToken(have);
  };

  const getTheme = (): string => {
    const prefersDarkTheme = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const fallback = prefersDarkTheme ? "dark" : "light";
    try {
      return localStorage.getItem("theme") ?? fallback;
    } catch (_: any) {
      // can get security exception if browser disallows localStorage
      return fallback;
    }
  };

  setTheme(getTheme(), false);

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <QueryClientProvider client={queryClient}>
        <HourglassInitialLoad haveToken={haveToken} onTokenChange={handleTokenChange} />
      </QueryClientProvider>
    </Suspense>
  );
}

function HourglassInitialLoad(props: InitialLoadProps) {
  const { t } = useTranslation();

  // if the browser doesn't support cookies, bail
  if (!supportsCookies()) {
    return <h5>{t("unsupported-browser.no-cookies.body")}</h5>;
  }

  if (!props.haveToken) {
    return <HourglassNotAuthenticated haveToken={props.haveToken} onTokenChange={props.onTokenChange} />;
  }

  return <HourglassAuthenticated />;
}

function HourglassNotAuthenticated(props: InitialLoadProps) {
  useEffect(() => {
    clearSignOutFlag();
  }, []);

  return (
    <BrowserRouter basename={HourglassGlobals.AppBase}>
      <Routes>
        <Route
          path="/invite/admin/accept/:code"
          element={<InviteAccept admin />}
          errorElement={<RouterErrorBoundary />}
        />
        <Route path="/invite/accept/:code" element={<InviteAccept />} errorElement={<RouterErrorBoundary />} />
        <Route path="/invite/co/accept/:code" element={<InviteAccept />} errorElement={<RouterErrorBoundary />} />
        <Route path="/invite/smpw/accept/:code" element={<InviteAccept />} errorElement={<RouterErrorBoundary />} />
        <Route path="/forgotpw/:code" element={<ForgotPWCode />} errorElement={<RouterErrorBoundary />} />
        <Route path="/register" element={<CongRegistration />} errorElement={<RouterErrorBoundary />} />
        <Route path="/coreg" element={<CoRegistration />} errorElement={<RouterErrorBoundary />} />
        <Route
          path="/smpwreg"
          element={<SMPWPreRegistration setHaveToken={props.onTokenChange} />}
          errorElement={<RouterErrorBoundary />}
        />
        <Route path="/smpwuserreg" element={<SMPWUserRegistrationLazy />} errorElement={<RouterErrorBoundary />} />
        <Route path="/view/territory/:token" element={<SharedTerritory />} errorElement={<RouterErrorBoundary />} />
        {/*make sure to this path is *, not /. It allows deep links to keep working when somebody needs to log in.*/}
        <Route
          path="*"
          element={<HourglassLogin setHaveToken={props.onTokenChange} />}
          errorElement={<RouterErrorBoundary />}
        />
      </Routes>
    </BrowserRouter>
  );
}

function HourglassAuthenticated() {
  const { i18n, t } = useTranslation();
  const [gotStartup, setGotStartup] = useState(false);
  const [populatedUsers, setPopulatedUsers] = useState(false);
  const [ctxState, setCtxState] = useState<GlobalContext>({
    globals: emptyGlobals(),
    setGlobals: () => {
      return;
    },
  });

  const updateGlobals = (newGlobals: HGGlobals) => {
    HourglassGlobals.setGlobals(newGlobals);
    setCtxState({ globals: newGlobals, setGlobals: updateGlobals });
  };

  const startupQuery = useQuery({
    queryKey: [QueryKeys.Startup],
    queryFn: () => startupApi.get(),
    staleTime: Infinity,
  });

  //not using QueryStatus here because we want isLoading to be SuspenseFallback, not a spinner
  if (startupQuery.isError) {
    return <span>{startupQuery.error.message}</span>;
  }
  if (startupQuery.isLoading) {
    return <SuspenseFallback />;
  }

  if (startupQuery.isSuccess && startupQuery.data && !gotStartup) {
    setGotStartup(true);
    const startup = startupQuery.data;
    const whoami = startup.whoami;
    if (!whoami || !whoami.user || !whoami.user.id) {
      Bugsnag.notify(new Error("startup whoami"), function (event) {
        event.context = "startupFailure";
        event.addMetadata("hg", {
          startup: !!startup,
          whoami: !!whoami,
          whoamiUser: !!whoami?.user,
          whoamiUserId: whoami?.user?.id,
        });
      });
      return (
        <span>
          {t("form.error.try-later")}
          <pre>error: whoami</pre>
        </span>
      );
    }

    Bugsnag.setUser(whoami.user.id.toString());

    const nameFmt = stringToNameFormat(whoami.congregation?.country.namefmt ?? "");

    const globals: HGGlobals = {
      cong: whoami.congregation,
      smpwRootZone: whoami.smpw_root_zone,
      staticUrl: startup.staticUrl,
      nameFmt: nameFmt,
      authUser: whoami.user,
      delegateFor: whoami.delegate_for
        ? whoami.delegate_for.map((du) => {
            return { ...du, displayName: nameOfUser(du, nameFmt) };
          })
        : [],
      permissions: new Set<string>(whoami.permissions),
      permissionGroups: startup.permissionGroups,
      oldestReport: startup.oldestReport,
      lastSubmittedMonth: startup.lastSubmittedMonth,
      workingMonth: startup.workingMonth,
      reportableServiceYears: startup.reportableServiceYears,
      flags: startup.flags,
      warningsDismissed: false,
      featureDismissed: false,
      featureFlags: whoami.featureFlags || {},
      missingCongTerritory: false,
      language_groups: whoami.language_groups || [],
      labelAsPreferredName: startup.labelAsPreferredName,
      whoamiSettings: whoami.settings,
      hasInfoBoard: whoami.hasInfoBoard,
      autoSubmitReportsBefore: new Map<number, Month>(),
      smpwInvites: whoami.smpw_invites,
    };

    HourglassGlobals.setGlobals(globals);
    UserAPI.userFromResponse(whoami.user).then((u) => {
      globals.authUser = u;
      HourglassGlobals.setGlobals(globals);
    });

    const uiLocale = localeForUser(whoami.congregation?.locale.code ?? "en", globals.authUser.locales_id);
    if (i18n.language !== uiLocale && globals.authUser.locales_id) {
      // if the detected language (based on the user's browser settings) doesn't match the configured language for the user,
      // and they have set a specific user locale, then use it.
      i18n.changeLanguage(uiLocale).then();
    }

    setCtxState({
      globals: globals,
      setGlobals: updateGlobals,
    });

    //cache the data we received
    queryClient.setQueryData<Report[]>(reportsQueryKey(whoami.user.id), whoami.reports);
    if (!globals.cong?.e2ekey) {
      //we don't have the unwrapped key yet - we're prompting the user for the password
      //a future optimization could save the encrypted info to a separate cache key then decrypt it once we
      //unwrap, but not needed right now
      if (startup.users) {
        const userPromises: Promise<User>[] = startup.users.map((u) => UserAPI.userFromResponse(u));
        Promise.all(userPromises).then((users) => {
          queryClient.setQueryData<User[]>([QueryKeys.Users], users);
          setPopulatedUsers(true);
        });
      }

      if (startup.addresses) {
        const addrPromises: Promise<Address>[] = startup.addresses.map((a) => AddressAPI.addressFromResponse(a));
        Promise.all(addrPromises).then((addresses) =>
          queryClient.setQueryData<Address[]>([QueryKeys.Addresses], addresses),
        );
      }
    } else {
      //MUST set this here - otherwise an e2e login just spins forever
      setPopulatedUsers(true);
    }
    queryClient.setQueryData<FSGroup[]>([QueryKeys.FSGroups], startup.fsGroups);
  }

  // resume an in progress smpw zone registration
  if (localStorage.getItem("smpwreg") === "true") {
    localStorage.removeItem("smpwreg");

    // if by chance the user has a zone assignment
    // then this is no longer needed.
    if (!ctxState.globals.authUser.smpw_root_zone_id) {
      window.location.href = "/v2/page/app/smpwreg";
    }
  }

  // User does NOT have a _mananged_ congregataion assigned
  if (HourglassGlobals.cong?.unmanaged ?? true) {
    const isCO = ctxState.globals.authUser.status === UserStatus.CO;
    const inZone = !!ctxState.globals.authUser.smpw_root_zone_id;

    return (
      <HGContext.Provider value={ctxState}>
        <BrowserRouter basename={HourglassGlobals.AppBase}>
          <HourglassUserNav />
          <Routes>
            {isCO && <Route path="/" element={<CircuitLazy />} errorElement={<RouterErrorBoundary />} />}
            {(isCO || inZone) && <Route path="/user/auth/:id" element={<ManageUserAuthLazy />} />}
            {inZone && <Route path="/smpw/*" element={<SMPWRoutes />} errorElement={<RouterErrorBoundary />} />}
            {!inZone && (
              <Route path="/smpwreg" element={<SMPWRegistrationLazy />} errorElement={<RouterErrorBoundary />} />
            )}
            <Route path="*" element={<Navigate to="/" />} errorElement={<RouterErrorBoundary />} />
          </Routes>
        </BrowserRouter>
      </HGContext.Provider>
    );
  }

  // User hsa a _mananged_ congregataion assigned, but no cong privs
  const hasNoCongPermissions = (): boolean => {
    if (hasPermission(Permission.ViewWeekendSpeakers)) return false;
    if (ctxState.globals.permissions.size <= 1) return true;
    // a user could have smpw permissions, but no cong permissions
    return Array.from(ctxState.globals.permissions).every((k) => k.startsWith("SMPW"));
  };
  if (hasNoCongPermissions()) {
    //regular user, or possibly can enter attendance
    return (
      <HGContext.Provider value={ctxState}>
        <BrowserRouter basename={HourglassGlobals.AppBase}>
          <HourglassUserNav />
          <Routes>
            {!!ctxState.globals.authUser.smpw_root_zone_id && (
              <Route path="/smpw/*" element={<SMPWRoutes />} errorElement={<RouterErrorBoundary />} />
            )}
            {!ctxState.globals.authUser.smpw_root_zone_id && (
              <Route path="/smpwreg" element={<SMPWRegistrationLazy />} errorElement={<RouterErrorBoundary />} />
            )}
            <Route path="/attendance" element={<MeetingAttendanceLazy />} errorElement={<RouterErrorBoundary />} />
            <Route path="/user/auth/:id" element={<ManageUserAuthLazy />} errorElement={<RouterErrorBoundary />} />
            <Route path="/territory/:id" element={<PublisherTerritory />} errorElement={<RouterErrorBoundary />} />
            <Route
              path="/infoboard"
              element={
                <div className="ms-4">
                  <InfoBoardLazy />
                </div>
              }
              errorElement={<RouterErrorBoundary />}
            />
            <Route path="/" element={<RegularPublisherHome />} errorElement={<RouterErrorBoundary />} />
            <Route path="*" element={<Navigate to="/" />} errorElement={<RouterErrorBoundary />} />
          </Routes>
        </BrowserRouter>
      </HGContext.Provider>
    );
  }

  if (ctxState.globals.cong?.e2ekey && !ctxState.globals.e2eKey) {
    //e2e but we haven't unwrapped the key. prompt for master password
    return (
      <HGContext.Provider value={ctxState}>
        <E2EMasterPassword />
      </HGContext.Provider>
    );
  }

  //because the Promise.all above for populating users may not have resolved yet, we stay in suspense until it's done
  //this prevents us from getting the users list right after startup, which is wasteful since all users are included in
  //the startup data
  const canGetUsers = hasPermission(Permission.ViewUsers) || hasPermission(Permission.ViewUsersMinimal);
  if (!gotStartup || (canGetUsers && !populatedUsers)) {
    return <SuspenseFallback />;
  }

  //this is the main/primary app: when a user with permissions logs in
  return (
    <HGContext.Provider value={ctxState}>
      <AuthRouter />
    </HGContext.Provider>
  );
}

function initialSetup(): boolean {
  let haveToken = haveCookie(XSRFHeader);
  if (!haveToken && import.meta.env.VITE_DEV_USER && import.meta.env.VITE_DEV_PASSWORD) {
    //make local dev easier by not requiring login all the time
    haveToken = true;
  }

  if (hasSignOutFlag()) {
    haveToken = false;
  }
  return haveToken;
}

export default App;
